import React, { createContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { getErrorMessage } from '../utils/errorHandler';
import { isBackendAvailable, checkBackendHealth } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backendOnline, setBackendOnline] = useState(true);
  const [enableStockCheckBrandDeletion, setEnableStockCheckBrandDeletion] = useState(() => {
    const saved = localStorage.getItem('enableStockCheckBrandDeletion');
    return saved ? JSON.parse(saved) : false;
  });

  const updateEnableStockCheckBrandDeletion = (val) => {
    setEnableStockCheckBrandDeletion(val);
    localStorage.setItem('enableStockCheckBrandDeletion', JSON.stringify(val));
  };

  useEffect(() => {
    // Check backend health first
    checkBackendHealth().then((health) => {
      setBackendOnline(health.online);
    });

    // Check for existing token
    const token = localStorage.getItem('token');
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }

    // Periodic backend health check
    const healthInterval = setInterval(async () => {
      const health = await checkBackendHealth();
      setBackendOnline(health.online);
    }, 10000); // Check every 10 seconds

    return () => clearInterval(healthInterval);
  }, []);

  const fetchCurrentUser = async () => {
    try {
      // Check if backend is available
      if (!isBackendAvailable()) {
        const health = await checkBackendHealth();
        if (!health.online) {
          setBackendOnline(false);
          setLoading(false);
          // Keep token for retry when backend comes back
          return;
        }
      }

      const response = await api.get('/auth/me');
      setUser(response.data.user);
      setBackendOnline(true);
    } catch (error) {
      // Only clear token if it's an auth error, not network error
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        setUser(null);
      } else if (error.isNetworkError) {
        // Backend is offline, keep token and user state
        setBackendOnline(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (phone, password, selectedRole) => {
    try {
      // Check backend health before login
      if (!isBackendAvailable()) {
        const health = await checkBackendHealth();
        if (!health.online) {
          setBackendOnline(false);
          return {
            success: false,
            error: 'ಸರ್ವರ್ ಸಂಪರ್ಕವಿಲ್ಲ. ದಯವಿಟ್ಟು ನಂತರ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.'
          };
        }
      }

      const response = await api.post('/auth/login', { 
        phone, 
        password, 
        selected_role: selectedRole 
      });
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setUser(user);
      setBackendOnline(true);
      return { success: true };
    } catch (error) {
      setBackendOnline(isBackendAvailable());
      
      // Provide user-friendly error message
      const errorMessage = error.isNetworkError 
        ? 'ಸರ್ವರ್ ಸಂಪರ್ಕವಿಲ್ಲ. ದಯವಿಟ್ಟು ನಂತರ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.'
        : getErrorMessage(error) || error.response?.data?.error || 'ಲಾಗಿನ್ ವಿಫಲವಾಗಿದೆ';
      
      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const logout = async () => {
    try {
      // Try to logout on backend if available
      if (isBackendAvailable()) {
        await api.post('/auth/logout');
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with local logout even if backend fails
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      setBackendOnline(true); // Reset status for next login
    }
  };

  // Helper function to check if user has permission for a module
  const hasPermission = (module, permission) => {
    if (!user) return false;
    // Admin always has all permissions
    if (user.isAdmin || user.currentRole === 'Admin') return true;
    
    // Check permissionsByModule from user object
    if (user.permissionsByModule && user.permissionsByModule[module]) {
      return user.permissionsByModule[module].includes(permission);
    }
    
    return false;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    backendOnline,
    // Scoped to the role selected at login (currentRole), not the account's
    // full roles list - a Manager+Staff account must act as Manager when
    // that's the role they signed in with, not be permanently Staff-restricted.
    isAdmin: user?.currentRole === 'Admin' || user?.isAdmin === true,
    isManager: user?.currentRole === 'Manager' || user?.isManager === true,
    isStaff: user?.currentRole === 'Staff' || user?.isStaff === true,
    currentRole: user?.currentRole || null,
    hasPermission: hasPermission,
    permissionsByModule: user?.permissionsByModule || {},
    shopSettings: user?.shop_settings || null,
    enableStockCheckBrandDeletion,
    setEnableStockCheckBrandDeletion: updateEnableStockCheckBrandDeletion
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

